import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Bot, Loader2, Sparkles, MessageSquare, Maximize2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API = '/api';

const AssistantPanel = ({ onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    // Focus input when panel opens
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    
    const userMessage = input.trim();
    setInput('');
    setLoading(true);
    
    // Add user message immediately
    const tempUserMsg = {
      id: `temp_${Date.now()}`,
      role: 'user',
      content: userMessage
    };
    setMessages(prev => [...prev, tempUserMsg]);
    
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: userMessage,
          conversation_id: conversationId
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setConversationId(data.conversation_id);
        
        // Add assistant response
        const assistantMsg = {
          id: `resp_${Date.now()}`,
          role: 'assistant',
          content: data.response
        };
        setMessages(prev => [...prev, assistantMsg]);
        
        // Check for pending actions
        if (data.pending_actions?.length > 0) {
          toast.info('Action requires confirmation', {
            description: 'Check the AI page to confirm the action',
            action: {
              label: 'Go to AI',
              onClick: () => navigate('/ai')
            }
          });
        }
      } else {
        const error = await res.json();
        toast.error('AI Error', { description: error.detail });
      }
    } catch (err) {
      console.error('Chat error:', err);
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatContent = (content) => {
    if (!content) return null;
    return content.split('\n').map((line, i) => {
      if (line.startsWith('- ') || line.startsWith('* ')) {
        return <li key={i} className="ml-3 text-sm">{line.slice(2)}</li>;
      }
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={i} className="font-semibold text-sm">{line.slice(2, -2)}</p>;
      }
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="text-sm mb-1">{line}</p>;
    });
  };

  return (
    <div className="fixed right-6 bottom-6 w-96 h-[500px] bg-background rounded-2xl shadow-2xl border flex flex-col z-50 overflow-hidden" data-testid="assistant-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <Bot className="w-4 h-4" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Orka AI</h3>
            <p className="text-xs text-white/70">Your AI Assistant</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 text-white hover:bg-white/20" 
            onClick={() => navigate('/ai')}
            title="Open full AI page"
          >
            <Maximize2 className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 text-white hover:bg-white/20" 
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-semibold mb-2">How can I help?</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-[200px]">
              Ask me about your tasks, schedule meetings, or get productivity insights.
            </p>
            <div className="space-y-2 w-full">
              {[
                "What's on my agenda today?",
                "Show my high priority tasks",
                "Create a task for tomorrow",
                "Give me a productivity summary"
              ].map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => setInput(suggestion)}
                  className="w-full text-left text-sm px-3 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                >
                  <MessageSquare className="w-3 h-3 inline mr-2 text-violet-500" />
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <div 
                key={msg.id} 
                className={cn(
                  "flex",
                  msg.role === 'user' ? "justify-end" : "justify-start"
                )}
              >
                <div className={cn(
                  "max-w-[85%] rounded-2xl px-4 py-2",
                  msg.role === 'user' 
                    ? "bg-gradient-to-br from-violet-600 to-indigo-600 text-white" 
                    : "bg-muted"
                )}>
                  {formatContent(msg.content)}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-2xl px-4 py-2">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything..."
            className="flex-1 rounded-full"
            disabled={loading}
            data-testid="assistant-input"
          />
          <Button 
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            className="rounded-full w-10 h-10 p-0 bg-gradient-to-br from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"
            data-testid="assistant-send"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssistantPanel;
